import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import {
  addCompanyAsset,
  deleteCompanyAsset,
  getArchivedCompanyAssets,
  getAssetSubtypes,
  getAssetTypes,
  getCompanyAssets,
  restoreCompanyAsset,
} from "../../repository/data.repository";
import {
  AssetSubtype,
  AssetType,
  Company,
  CompanyAsset,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { retry, getProblemMessage } from "../../utils/http";
import ComboBox from "../../controls/ComboBox";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface CompanyAssetsPanelProps {
  readonly open: boolean;
  readonly companyUid: string | undefined;
  readonly companyName?: string;
  readonly reloadToken?: string | number;
  readonly onChange?: () => void;
  readonly onCountLoaded?: (count: number) => void;
  readonly scopeCompanies: Company[];
}

/**
 * Assets across the whole branch (root + descendants). Add dialog picks the target company so the
 * operator can place vessels / sites / aircraft on any sub-company without leaving this card. The
 * legacy standalone CompanyAssetsView is not touched; it still handles the "Manage" link in the
 * company's static Assets descriptions row.
 */
function CompanyAssetsPanel(props: CompanyAssetsPanelProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const canCreate = permissions.can("CompanyAssets", "create");
  const canDelete = permissions.can("CompanyAssets", "delete");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CompanyAsset[]>([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringUid, setRestoringUid] = useState<string | null>(null);
  const everLoadedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scopeUids = useMemo(
    () => props.scopeCompanies.map((c) => c.uid),
    [props.scopeCompanies],
  );
  const scopeKey = scopeUids.join("|");

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    load(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, scopeKey, props.reloadToken, showArchived]);

  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function load(nameFilter: string): void {
    if (scopeUids.length === 0) return;
    setLoading(true);
    const conditions: any[] = [
      {
        field: nameOf<CompanyAsset>("companyUid"),
        values: scopeUids,
        operation: QueryOperation.In,
      },
    ];
    if (nameFilter.trim()) {
      conditions.push({
        field: nameOf<CompanyAsset>("name"),
        values: [nameFilter.trim()],
        operation: QueryOperation.StartsWith,
      });
    }
    const loader = showArchived ? getArchivedCompanyAssets : getCompanyAssets;
    retry(() =>
      loader({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then(({ data }) => {
        setItems(data);
        setLoading(false);
        everLoadedRef.current = true;
        if (!showArchived) props.onCountLoaded?.(data.length);
      })
      .catch(() => setLoading(false));
  }

  async function onRestore(uid: string): Promise<void> {
    try {
      setRestoringUid(uid);
      await restoreCompanyAsset(uid);
      message.success("Asset restored.");
      setRestoringUid(null);
      load(search);
      props.onChange?.();
    } catch (err) {
      setRestoringUid(null);
      message.error(getProblemMessage(err));
    }
  }

  function onMutated(): void {
    setAddOpen(false);
    load(search);
    props.onChange?.();
  }

  async function onDelete(uid: string): Promise<void> {
    try {
      await deleteCompanyAsset(uid);
      message.success("Asset deleted.");
      onMutated();
    } catch (err) {
      message.error(getProblemMessage(err));
    }
  }

  if (!props.open) return null;

  const showSkeleton = loading && !everLoadedRef.current;
  const multiCompany = props.scopeCompanies.length > 1;

  return (
    <Card
      size="small"
      title={
        <Space>
          <Typography.Text strong>
            {multiCompany ? "Assets (whole branch)" : "Assets"}
          </Typography.Text>
          <Typography.Text type="secondary">({items.length})</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          {/* Admin brief 2026-11-03 — toggle to list archived (soft-deleted)
              assets inside the branch + restore. */}
          <Typography.Text type="secondary" className="text-xs">
            Show archived
          </Typography.Text>
          <Switch
            size="small"
            checked={showArchived}
            onChange={setShowArchived}
          />
          <Input.Search
            placeholder="Search by name…"
            allowClear
            size="small"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="primary"
            size="small"
            disabled={!canCreate || !props.companyUid || showArchived}
            onClick={() => setAddOpen(true)}
          >
            + Add Asset
          </Button>
        </Space>
      }
    >
      {showSkeleton ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <Table
          size="small"
          loading={loading}
          rowKey={(r) => r.uid}
          dataSource={items}
          pagination={items.length > 10 ? { pageSize: 10 } : false}
          locale={{
            emptyText: showArchived
              ? "No archived assets."
              : "No assets yet.",
          }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            { title: "Code", dataIndex: "code", key: "code" },
            { title: "Type", dataIndex: "assetTypeName", key: "assetTypeName" },
            { title: "Subtype", dataIndex: "assetSubtypeName", key: "assetSubtypeName" },
            ...(multiCompany
              ? [{ title: "Company", dataIndex: "companyName", key: "companyName" }]
              : []),
            { title: "Users", dataIndex: "usersCount", key: "usersCount", width: 80 },
            {
              title: "",
              key: "actions",
              width: 110,
              render: (_: any, record: CompanyAsset) =>
                showArchived ? (
                  <Popconfirm
                    title="Restore this asset?"
                    description="It will reappear in the active list."
                    okText="Restore"
                    onConfirm={() => onRestore(record.uid)}
                  >
                    <Button
                      size="small"
                      type="link"
                      loading={restoringUid === record.uid}
                    >
                      Restore
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="Archive this asset?"
                    description="It will be hidden from the active list — data stays for history."
                    okText="Archive"
                    okButtonProps={{ danger: true }}
                    disabled={!canDelete}
                    onConfirm={() => onDelete(record.uid)}
                  >
                    <Button
                      size="small"
                      type="link"
                      danger
                      disabled={!canDelete}
                    >
                      Archive
                    </Button>
                  </Popconfirm>
                ),
            },
          ]}
        />
      )}
      <AddAssetDialog
        open={addOpen}
        scopeCompanies={props.scopeCompanies}
        defaultCompanyUid={props.companyUid}
        onCancel={() => setAddOpen(false)}
        onFinish={onMutated}
      />
    </Card>
  );
}

function AddAssetDialog(props: {
  open: boolean;
  scopeCompanies: Company[];
  defaultCompanyUid: string | undefined;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError("");
    } else {
      const def = props.scopeCompanies.find((c) => c.uid === props.defaultCompanyUid);
      if (def) form.setFieldsValue({ company: { display: def.name, value: def.uid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const chosenAssetTypeUid: string | undefined = values?.assetType?.value;

  const companyRequest = (search: string) => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.scopeCompanies.filter((c) => c.name.toLowerCase().startsWith(q))
      : props.scopeCompanies;
    return Promise.resolve(
      filtered.map((c) => ({ display: c.name, value: c.uid, dataItem: c })),
    );
  };

  const assetTypesRequest = (search: string) =>
    retry(() =>
      getAssetTypes({
        searchQuery: {
          conditions: [
            { field: nameOf<AssetType>("name"), values: [search], operation: QueryOperation.StartsWith },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })));

  const assetSubtypesRequest = (search: string) => {
    if (!chosenAssetTypeUid) return Promise.resolve([]);
    return retry(() =>
      getAssetSubtypes({
        searchQuery: {
          conditions: [
            { field: nameOf<AssetSubtype>("assetTypeUid"), values: [chosenAssetTypeUid], operation: QueryOperation.Equal },
            { field: nameOf<AssetSubtype>("name"), values: [search], operation: QueryOperation.StartsWith },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) => data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })));
  };

  async function onSubmit(): Promise<void> {
    try {
      const vs = await form.validateFields();
      setSaving(true);
      setError("");
      await addCompanyAsset({
        name: vs.name,
        code: vs.code || undefined,
        assetTypeUid: vs.assetType?.value,
        assetSubtypeUid: vs.assetSubtype?.value,
        companyUid: vs.company?.value,
      });
      message.success(`Asset "${vs.name}" added to "${vs.company?.display}".`);
      setSaving(false);
      props.onFinish();
    } catch (err: any) {
      setSaving(false);
      if (err?.errorFields) return;
      setError(getProblemMessage(err));
    }
  }

  return (
    <Modal
      open={props.open}
      title="Add Asset"
      onCancel={props.onCancel}
      onOk={onSubmit}
      okText="Create"
      confirmLoading={saving}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="company"
          label="Register for (company)"
          rules={[{ required: true, message: "Company is required" }]}
        >
          <ComboBox request={companyRequest} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="code" label="Code (optional)">
          <Input />
        </Form.Item>
        <Form.Item
          name="assetType"
          label="Asset Type"
          rules={[{ required: true, message: "Asset type is required" }]}
        >
          <ComboBox request={assetTypesRequest} />
        </Form.Item>
        <Form.Item name="assetSubtype" label="Asset Subtype (optional, scoped to type)">
          <ComboBox request={assetSubtypesRequest} />
        </Form.Item>
      </Form>
      {error && (
        <Typography.Text type="danger" className="mt-2 block">
          {error}
        </Typography.Text>
      )}
    </Modal>
  );
}

export default CompanyAssetsPanel;
